<template>
	<article class="job">
		<div class="salary-age-container">
			<span class="bubble green small">
				<span>{{ item.salary ? item.salary : 'Salary Unstated' }}</span>
			</span>

			<span v-if="item.age" class="age">
				<v-icon slug="clock" />
				<span>{{ item.age }}</span>
			</span>
		</div>

		<h4>
			<a v-if="disable_modal" :href="item.canonical_url" class="Activate-Result-Job-Title btn">
				{{ title }}
			</a>

			<button v-else type="button" class="Activate-Result-Job-Title btn" @click="open">
				{{ title }}
			</button>
		</h4>

		<div class="company-details">
			<company-logo :item="item" :logo_url_template="logo_url_template" />

			<div>
				<p v-if="item.company_name" class="company-name">
					<span>{{ item.company_name }}</span>
				</p>

				<p v-if="item.location" class="company-location">
					<v-icon slug="location-dot" />
					<span>{{ item.location }}</span>
				</p>
			</div>
		</div>

		<div class="bubbles">
			<span v-for="schedule of item.display_work_schedules" :key="schedule" class="bubble orange small">
				<span>{{ schedule.replace(/\((.+?)\)/, '') }}</span>
			</span>

			<span
				v-for="employment_type of item.display_employment_types"
				:key="employment_type"
				class="bubble orange small"
			>
				<span>{{ employment_type.replace(/\((.+?)\)/, '') }}</span>
			</span>

			<span v-if="item.display_work_environment" class="blue bubble small">
				<span>{{ item.display_work_environment }}</span>
			</span>
		</div>

		<p v-if="summary && !hide_summary" class="summary">
			<strong>Summary: </strong>

			{{ summary }}

			<a :href="item.canonical_url" class="Activate-Result-View-Job-Description btn btn-link"
				>View Job Description</a
			>
		</p>

		<ul v-if="item.technologies.length > 0" class="technologies">
			<li v-for="(technology, idx) of item.technologies" :key="idx" class="bubble green light small">
				<span>{{ technology }}</span>
			</li>
		</ul>

		<footer v-if="!hide_apply_button">
			<button type="button" class="Activate-Result-Apply-Now btn btn-solid" @click="open">Apply Now</button>
		</footer>

		<footer v-else-if="disable_modal" class="view-job-description">
			<a
				v-if="disable_modal"
				:href="item.canonical_url"
				class="Activate-Result-View-Job-Description btn btn-link"
			>
				<span>View Job Description</span>
				<v-icon slug="circle-arrow-right" />
			</a>
		</footer>

		<slot name="deck-footer" />
	</article>

	<modal v-if="!disable_modal" :item="item" :is_open="is_open" @close="close" />
</template>

<style lang="scss" scoped>
@use 'sass:math';

@use '/sass/variables' as *;
@use '/sass/toolkit' as *;

.job {
	background-color: $white;
	padding: $spacer;
	border: 1px solid $grey-border;
	border-radius: $spacer;
	display: flex;
	flex-direction: column;
	min-width: 0;
}

.salary-age-container {
	display: flex;
	align-items: center;
	gap: $spacer;
	margin-bottom: 1rem;
}

h4 {
	@include h5;
	border-bottom: 1px solid $grey-border;
	padding-bottom: 1rem;
	margin-bottom: 1rem;

	.btn {
		@include underline;
		font-weight: inherit;
		text-align: left;
	}
}

.company-details {
	display: flex;
	align-items: center;
	gap: math.div($spacer, 2);
	margin-bottom: 1rem;

	> img {
		display: block;
		flex: 0 0 auto;
		border: 2px solid $grey-border;
		border-radius: 0.5em;
	}
}

.company-name {
	margin-bottom: 0;
	font-weight: bold;
	color: $black;
	font-size: 0.875rem;
}

.company-location {
	font-size: 0.75rem;
	display: flex;
	align-items: center;
	gap: 0.25rem;

	svg {
		width: 1rem;
		height: 1rem;
		flex: 0 0 auto;
		display: block;
	}
}

.bubbles {
	display: flex;
	align-items: center;
	gap: 0.25em;
	flex-wrap: wrap;
	margin-bottom: 1rem;

	&:last-child {
		margin-bottom: 0;
	}
}

.summary {
	font-size: 0.875rem;
}

.technologies {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 0.25em;
	margin-bottom: 1rem;

	&:last-child {
		margin-bottom: 0;
	}
}

footer {
	margin-top: auto;

	.btn {
		width: 100%;
	}
}

.view-job-description {
	border-top: 1px solid $grey-border;
	padding-top: 1rem;
	// font-weight: bold;

	&:hover {
		svg {
			transform: rotate(-45deg);
			color: $orange;
		}
	}

	svg {
		margin-left: auto;
		transition: inherit;
		width: 1.5rem;
		height: 1.5rem;
	}
}
</style>

<script setup>
/* eslint-disable vue/no-boolean-default */

import { computed, ref } from 'vue';

import CompanyLogo from './_company-logo.vue';
import VIcon from './_v-icon.vue';
import Modal from './_v-modal.vue';

const props = defineProps({
	item: {
		type: Object,
		required: true,
	},
	hide_summary: {
		type: Boolean,
	},
	hide_apply_button: {
		type: Boolean,
	},
	logo_url_template: {
		type: String,
		required: false,
		default: null,
	},
	disable_modal: {
		type: Boolean,
		required: false,
		default: false,
	},
});

const is_open = ref(false);

function open() {
	is_open.value = true;
}

function close() {
	is_open.value = false;
}

const title = computed(() => {
	const max = 60;

	if (props.item.title === ``) {
		return;
	}

	const len = props.item.title.length;
	if (len < max) {
		return props.item.title;
	}

	return `${props.item.title.slice(0, max - 3)}...`;
});

const summary = computed(() => {
	const max = 280;

	if (props.item.summary === ``) {
		return;
	}

	const len = props.item.summary.length;
	if (len < max) {
		return props.item.summary;
	}

	return `${props.item.summary.slice(0, max - 3)}...`;
});
</script>
